
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, Zap } from "lucide-react";
import type { Question } from "@/types/test";
import { isQuestionContent } from "@/types/test";

interface AIGenerationWarningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  question?: Question;
  jobContext?: {
    title?: string;
    description?: string;
    keySkills?: string[];
    yearsOfExperience?: string;
  };
}

export const AIGenerationWarningDialog = ({
  open,
  onOpenChange,
  onConfirm,
  question,
  jobContext,
}: AIGenerationWarningDialogProps) => {
  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-[500px]">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-blue-600" />
            Generate Markscheme with AI
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800 dark:text-amber-200 mb-1">
                    This will overwrite your current markscheme
                  </p>
                  <p className="text-amber-700 dark:text-amber-300">
                    Any existing criteria, guidance, and suggested answers will be replaced.
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-medium text-foreground">AI will use this information:</h4>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>Question: {question?.content && isQuestionContent(question.content) && question.content.question ? "Available" : "Not provided"}</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${question?.question_type ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                    <span>Question Type: {question?.question_type || "Not specified"}</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${question?.skill ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                    <span>Skill: {question?.skill || "Not specified"}</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${jobContext?.title ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                    <span>Job Title: {jobContext?.title || "Not provided"}</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${jobContext?.keySkills?.length ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                    <span>Key Skills: {jobContext?.keySkills?.length ? `${jobContext.keySkills.length} skills` : "Not provided"}</span>
                  </div>
                </div>
              </div>

              <p className="text-sm text-muted-foreground">
                The more information you provide, the better the AI-generated markscheme will be.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} className="bg-blue-600 hover:bg-blue-700">
            Generate Markscheme
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
